import React, { useState } from 'react';
import { Upload, Video } from 'lucide-react';
import { getUploadUrlWithQuota } from '../../services/uploadWithQuota';

interface VideoBlockProps {
  data: any;
  onChange: (data: any) => void;
  onDelete?: () => void;
  isPreview?: boolean;
}

const VideoBlock: React.FC<VideoBlockProps> = ({ data, onChange, isPreview = false }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoLoadProgress, setVideoLoadProgress] = useState(0);

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      alert('กรุณาเลือกไฟล์วิดีโอเท่านั้น');
      return;
    }

    // Check file size (max 1GB)
    if (file.size > 1024 * 1024 * 1024) {
      alert('ไฟล์มีขนาดใหญ่เกิน 1GB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      console.log('[VideoBlock] Uploading video:', file.name, file.size);
      
      // Upload to Cloudflare R2 via Worker (same as LMS)
      const result = await getUploadUrlWithQuota(
        file.name,
        file.type,
        file.size,
        file,
        (progress) => {
          setUploadProgress(progress);
          console.log(`Upload progress: ${progress}%`);
        }
      );
      
      console.log('[VideoBlock] Upload successful:', result);
      
      onChange({
        url: result.publicUrl,
        fileName: file.name,
        fileSize: file.size,
        jobId: result.jobId || null
      });
      
      alert('✅ อัปโหลดวิดีโอสำเร็จ!');
    } catch (error: any) {
      console.error('Error uploading video:', error);
      alert(error.message || 'เกิดข้อผิดพลาดในการอัปโหลดวิดีโอ');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (!data?.url) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
        <label className="flex flex-col items-center cursor-pointer">
          <Video className="w-16 h-16 text-gray-400 mb-4" />
          <span className="text-gray-600 font-medium mb-2">คลิกเพื่ออัปโหลดวิดีโอ</span>
          <span className="text-sm text-gray-500">รองรับไฟล์ MP4, MOV, AVI</span>
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="hidden"
            disabled={uploading}
          />
        </label>
        {uploading && (
          <div className="mt-4">
            <div className="flex items-center justify-center mb-2">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-200 ease-out"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 text-center">กำลังอัปโหลด... {uploadProgress}%</p>
          </div>
        )}
      </div>
    );
  }

  const handleVideoLoadStart = () => {
    setVideoLoading(true);
    setVideoLoadProgress(0);
  };

  const handleVideoProgress = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (video.buffered.length > 0) {
      const bufferedEnd = video.buffered.end(video.buffered.length - 1);
      const duration = video.duration;
      if (duration > 0) {
        const progress = Math.round((bufferedEnd / duration) * 100);
        setVideoLoadProgress(progress);
      }
    }
  };

  const handleVideoCanPlay = () => {
    setVideoLoading(false);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg overflow-hidden relative">
        {/* Upload Progress Overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center z-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <div className="w-64 bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 font-semibold">กำลังอัปโหลดวิดีโอใหม่... {uploadProgress}%</p>
            <p className="text-xs text-gray-500 mt-1">กรุณารอสักครู่</p>
          </div>
        )}
        
        {/* Video Load Progress Overlay */}
        {videoLoading && !uploading && (
          <div className="absolute inset-0 bg-gray-100 flex flex-col items-center justify-center z-10">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <div className="w-64 bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${videoLoadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600">กำลังโหลดวิดีโอ... {videoLoadProgress}%</p>
          </div>
        )}
        
        <video
          src={data.url}
          controls
          playsInline
          controlsList="nodownload"
          className="w-full h-auto max-h-[600px]"
          style={{ maxWidth: '100%', display: 'block' }}
          onLoadStart={handleVideoLoadStart}
          onProgress={handleVideoProgress}
          onCanPlay={handleVideoCanPlay}
        >
          เบราว์เซอร์ของคุณไม่รองรับการเล่นวิดีโอ
        </video>
      </div>
      {!isPreview && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{data.fileName}</span>
          {!uploading ? (
            <label className="cursor-pointer text-blue-500 hover:text-blue-600 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          ) : (
            <span className="text-gray-400 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              กำลังอัปโหลด...
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoBlock;
